import App from 'antd/lib/app';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    checkFilterInvalidValues,
    countNotEmptyFilters,
    transformCmriOverviewSavedFilterValuesFromDto,
    transformCmriOverviewSavedFilterValuesToDto,
} from './utils';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from 'src/components/sidebar-filters/saved-filter/interface';
import { usePageLocation } from 'src/pages/usePageState';
import { DEFAULT_PAGINATION } from 'src/domain/commonConst';
import { useSidebarFilterOptionsContext } from 'src/pages/energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { useSavedFiltersList } from 'src/components/sidebar-filters/useSavedFiltersList';
import { useSavedFilterCreateMutation, useSavedFilterDeleteMutation, useSavedFilterUpdateMutation } from 'src/domain/saved-filters/queries';

export interface ICmriOverviewPageState {
    // Filters
    filter?: {
        source?: string[] | string;
        status?: string[] | string;
        resource?: string[] | string;
        hoursRange?: [number, number];
        mw?: [number, number];
        price?: [number, number];
        earnings?: [number, number];
    };
    search?: string;

    pagination?: {
        current: number;
        pageSize: number;
    };

    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export interface ICmriOverviewSidebarFilters {
    source?: string[];
    status?: string[];
    resource?: string[];
    hoursRange?: [number, number];
    mw?: [number, number];
    price?: [number, number];
    earnings?: [number, number];
}

export interface ICmriOverviewSidebarFiltersContext {
    filtersCount: number;
    filters: ICmriOverviewSidebarFilters;
    savedFilters: ISavedFilter[];

    isSavedFiltersLoading: boolean;

    handleFilterChange: (key: string, value: any) => void;
    handleFiltersChange: (newFilters: ICmriOverviewSidebarFilters) => void;
    handleFiltersClear: () => void;

    selectedSavedFilter: ICreateSavedFilter | null;
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;

    appliedSavedFilter: ISavedFilter | null; // Using for applying filters
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const CmriOverviewSidebarFiltersContext = React.createContext<ICmriOverviewSidebarFiltersContext>({} as ICmriOverviewSidebarFiltersContext);

export const convertCmriOverviewPageStateToFilterState = (pageState: ICmriOverviewPageState): ICmriOverviewSidebarFilters => {
    const result: ICmriOverviewSidebarFilters = {};
    const filterData = pageState.filter || {};

    if (filterData.source !== undefined) {
        result.source = Array.isArray(filterData.source) ? filterData.source : [filterData.source];
    }

    if (filterData.status !== undefined) {
        result.source = Array.isArray(filterData.status) ? filterData.status : [filterData.status];
    }

    if (filterData.resource !== undefined) {
        result.resource = Array.isArray(filterData.resource) ? filterData.resource : [filterData.resource];
    }

    if (filterData.hoursRange !== undefined && filterData.hoursRange.length === 2) {
        result.hoursRange = filterData.hoursRange;
    }

    if (filterData.mw !== undefined && filterData.mw.length === 2) {
        result.mw = filterData.mw;
    }

    if (filterData.price !== undefined && filterData.price.length === 2) {
        result.price = filterData.price;
    }

    if (filterData.earnings !== undefined && filterData.earnings.length === 2) {
        result.earnings = filterData.earnings;
    }

    console.info('convertCmriOverviewPageStateToFilterState', result);

    return result;
};

export const CmriOverviewSidebarFiltersProvider = ({ children }: { children: React.ReactNode }) => {
    const { filtersCount, filters, handleFilterChange, handleFiltersChange, handleFiltersClear } = useSidebarFiltering();

    // Saving Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    } = useCmriOverviewSavedFilters();

    const onFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when filters change
        },
        [handleApplySavedFilter, handleFilterChange]
    );

    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleFiltersChange]
    );

    const onFiltersClear = useCallback(() => {
        handleFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleFiltersClear]);

    return (
        <CmriOverviewSidebarFiltersContext.Provider
            value={{
                filtersCount,
                filters,
                savedFilters,

                isSavedFiltersLoading,

                handleFilterChange: onFiltersChange,
                handleFiltersChange: handleFiltersChange,
                handleFiltersClear: onFiltersClear,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </CmriOverviewSidebarFiltersContext.Provider>
    );
};

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: ICmriOverviewPageState = queryToState(location.search);
    const [filters, setFilters] = useState<ICmriOverviewSidebarFilters>(convertCmriOverviewPageStateToFilterState(pageState));
    const filtersCount = useMemo(() => countNotEmptyFilters(filters), [filters]);

    const handleFilterChange = (key: string, value: unknown) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // Update the URL query parameters with filter object structure
        const updatedPageState = {
            ...pageState,
            filter: {
                ...pageState.filter,
                [key]: value,
            },
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        };

        setPageQuery(updatedPageState);
    };

    const handleFiltersChange = (newFilters: ICmriOverviewSidebarFilters) => {
        setFilters(newFilters);
        setPageQuery({
            ...pageState,
            filter: newFilters,
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        });
    };

    const handleFiltersClear = () => {
        setFilters({});

        setPageQuery({
            ...pageState,
            filter: {},
        });
    };

    return {
        filtersCount,
        filters,

        handleFilterChange,
        handleFiltersChange,
        handleFiltersClear,
    };
};

const useCmriOverviewSavedFilters = () => {
    const { notification } = App.useApp();
    const context = useSidebarFilterOptionsContext();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.marketAwards);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            // Transform filter values from DTO format to Interface
            filter.filter_values = transformCmriOverviewSavedFilterValuesFromDto(filter.filter_values);

            const outdatedFields = checkFilterInvalidValues(filter.filter_values, context);
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [context]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map((filter: any) => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformCmriOverviewSavedFilterValuesToDto(filterData.filter_values);

            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformCmriOverviewSavedFilterValuesToDto(filterData.filter_values);

            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,
        validateSavedFilter,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    };
};

export const useCmriOverviewSidebarFiltersContext = () => {
    const context = React.useContext(CmriOverviewSidebarFiltersContext);
    if (!context) {
        throw new Error('useCmriOverviewSidebarFiltersContext must be used within CmriOverviewSidebarFiltersContext');
    }

    return context;
};
